import { useQuery } from "@rocicorp/zero/react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { CircleUser, Info } from "lucide-react";
import { debounce, isEqual, pick } from "radashi";
import { PropsWithChildren } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { TaskDescriptionInput } from "@/features/tasks/components/description-input";
import { TaskTitleInput } from "@/features/tasks/components/title-input";
import { useZero } from "@/hooks/use-zero";
import { Task } from "@/lib/types";

export const Route = createFileRoute("/_app/w/$slug/$boardKey/$taskNumber")({
  component: RouteComponent,
});

function isTheSame<T extends object>(data: T, updateData: Partial<T>) {
  return isEqual(
    pick(data, Object.keys(updateData) as (keyof T)[]),
    updateData,
  );
}

function RouteComponent() {
  const { boardKey, taskNumber } = Route.useParams();
  const zero = useZero();
  const [board, boardResult] = useQuery(
    zero.query.boards
      .where("boardKey", boardKey)
      .related("lists", (q) => q.orderBy("position", "asc"))
      .related("members")
      .one(),
  );
  const [task, taskResult] = useQuery(
    zero.query.tasks
      .where("boardId", board?.id ?? "")
      .where("taskNumber", parseInt(taskNumber, 10))
      .related("list")
      .related("assignees")
      .one(),
    { enabled: !!board },
  );

  const updateTask = debounce(
    {
      delay: 2000,
    },
    async (data: Partial<Task>) => {
      if (!task) {
        return;
      }
      if (isTheSame(task, data)) {
        return;
      }

      await zero.mutate.tasks.update({
        ...data,
        id: task.id,
      });
    },
  );

  if (!board) {
    if (boardResult.type === "complete") {
      throw notFound();
    }
    return null;
  }

  if (!task) {
    if (taskResult.type === "complete") {
      throw notFound();
    }
    return null;
  }

  const taskId = `${board.boardKey}-${task.taskNumber}`;

  return (
    <>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            try {
              navigator.clipboard.writeText(`${taskId}`);
              toast.info(`"${taskId}" copied to clipboard`, {
                icon: <Info className="size-4 text-muted-foreground" />,
              });
            } catch (error) {
              console.warn("Failed to copy to clipboard", error);
              toast.error(`Error copying to clipboard`);
            }
          }}
        >
          {taskId}
        </Button>
        <Combobox
          value={task.listId}
          onChange={(value) => {
            updateTask.flush({ listId: value });
          }}
          options={
            board.lists.map((l) => ({
              value: l.id,
              label: l.name,
            })) ?? []
          }
          noun={["status", "statuses"]}
        >
          <Button variant="outline" size="sm">
            {task.list!.name}
          </Button>
        </Combobox>
        <Combobox
          multiple
          value={task.assignees.map((a) => a.id)}
          onChange={(_, change) => {
            for (const added of change.added) {
              zero.mutate.tasks.addAssignee({
                taskId: task.id,
                userId: added,
              });
            }
            for (const removed of change.removed) {
              zero.mutate.tasks.removeAssignee({
                taskId: task.id,
                userId: removed,
              });
            }
          }}
          options={
            board.members.map((m) => ({
              value: m.id,
              label: m.name!,
            })) ?? []
          }
          noun={["assignee", "assignees"]}
          placeholder="Assign to..."
        >
          <Button variant="outline" size="sm">
            {task.assignees.length > 0 ? (
              task.assignees.map((a) => a.name ?? a.id).join(", ")
            ) : (
              <>
                <CircleUser className="size-4 text-muted-foreground" />
                <span className="text-muted-foreground">Assign</span>
              </>
            )}
          </Button>
        </Combobox>
        <DeleteTaskDialog task={task}>
          <Button variant="outline" size="sm">
            Delete task
          </Button>
        </DeleteTaskDialog>
      </div>
      <TaskTitleInput
        value={task.name}
        onChange={(value) => {
          updateTask({ name: value });
        }}
        onBlur={(value) => {
          updateTask.flush({ name: value });
        }}
      />
      <TaskDescriptionInput
        className="mt-3"
        value={task.description}
        onChange={(value) => {
          updateTask({ description: value });
        }}
        onBlur={(value) => {
          updateTask.flush({ description: value });
        }}
      />
    </>
  );
}

function DeleteTaskDialog({
  task,
  children,
}: PropsWithChildren<{ task: Task }>) {
  const zero = useZero();
  const navigate = Route.useNavigate();
  const onDelete = async () => {
    await navigate({ to: "..", replace: true });
    zero.mutate.tasks.delete({ id: task.id });
    toast.success("Task deleted");
  };

  return (
    <Drawer>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent>
        <div className="mx-auto w-full max-w-sm">
          <DrawerHeader>
            <DrawerTitle>Delete Task</DrawerTitle>
            <DrawerDescription>
              Are you sure you want to delete this task?
            </DrawerDescription>
          </DrawerHeader>
          <DrawerFooter>
            <Button variant="destructive" onClick={onDelete}>
              Delete task
            </Button>
            <DrawerClose asChild>
              <Button variant="outline">Cancel</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
